<div class="request-create">
  <div class="request-create__content">
    <div class="request-create__header">
      <div class="request-create__heading">
        <h1 class="page-title">Create Request</h1>
        <p class="page-subtitle">
          Build a draft request now, review stock context by warehouse, and reserve inventory later on confirm.
        </p>
      </div>
      <div class="request-create__actions">
        <app-button type="button" variant="secondary" (click)="goBack()">Back</app-button>
        <app-button type="button" [disabled]="isSaving || form.invalid || hasDuplicateMaterials" (click)="save()">
          {{ isSaving ? "Creating..." : "Create Request" }}
        </app-button>
      </div>
    </div>

    <div class="request-create__builder">
      <div class="request-create__main">
        <app-card>
          <section class="request-create__section">
            <div class="request-create__section-heading">
              <span class="request-create__section-kicker">1</span>
              <div class="request-create__section-heading-main">
                <h2 class="request-create__section-title">Request Setup</h2>
                <p class="request-create__section-copy">
                  Choose who is requesting, who they are requesting for, and which warehouse will fulfill it.
                </p>
              </div>
            </div>

            <form [formGroup]="form" class="request-create__form">
              <div class="request-create__form-grid">
                <div class="request-create__form-field">
                  <label for="request-pages-create-requestcreatepage-field-1" class="request-create__form-label">Team Leader</label>
                  <app-autocomplete
                    [prefetchOnInit]="true"
                    [options]="teamLeaderOptions"
                    [minLength]="1"
                    [debounceMs]="300"
                    placeholder="Search team leader..."
                    (searchChange)="searchTeamLeaders($event)"
                    (selectionChange)="onTeamLeaderChange($event)"></app-autocomplete>
                </div>

                <div class="request-create__form-field">
                  <label for="request-pages-create-requestcreatepage-field-2" class="request-create__form-label">Partner</label>
                  <app-autocomplete
                    [prefetchOnInit]="true"
                    [disabled]="!canSelectPartner"
                    [options]="partnerOptions"
                    [minLength]="0"
                    [debounceMs]="300"
                    placeholder="Search assigned partner..."
                    (searchChange)="searchPartners($event)"
                    (selectionChange)="onPartnerChange($event)"></app-autocomplete>
                  <span class="request-create__helper-text">{{ partnerHelperText }}</span>
                </div>
              </div>

              <div class="request-create__form-grid request-create__form-grid--single">
                <div class="request-create__form-field">
                  <label for="request-pages-create-requestcreatepage-field-3" class="request-create__form-label">Warehouse</label>
                  <app-autocomplete
                    [prefetchOnInit]="true"
                    [options]="warehouseOptions"
                    [minLength]="1"
                    [debounceMs]="300"
                    placeholder="Search warehouse..."
                    (searchChange)="searchWarehouses($event)"
                    (selectionChange)="onWarehouseChange($event)"></app-autocomplete>
                </div>
              </div>

              <div class="request-create__form-field">
                <label for="request-pages-create-requestcreatepage-field-4" class="request-create__form-label">Note</label>
                <app-rich-text-editor
                  formControlName="note"
                  placeholder="Add request context, delivery note, or constraints..."></app-rich-text-editor>
              </div>
            </form>
          </section>
        </app-card>

        <app-card>
          <section class="request-create__section">
            <div class="request-create__section-heading">
              <span class="request-create__section-kicker">2</span>
              <div class="request-create__section-heading-main">
                <h2 class="request-create__section-title">Request Items</h2>
                <p class="request-create__section-copy">
                  Add each material as its own request line and compare it against live warehouse availability.
                </p>
              </div>
              <app-button type="button" variant="secondary" class="request-create__section-heading-action" (click)="addItem()">
                <ng-glyph name="add" size="18"></ng-glyph>
                Add Line
              </app-button>
            </div>

            <div [formGroup]="form">
              <div formArrayName="items" class="request-create__items">
                @for (itemControl of items.controls; track $index) {
                <div [formGroup]="getItemForm($index)" class="request-create__item-card">
                  <div class="request-create__item-card-header">
                    <div>
                      <h3 class="request-create__item-title">Line {{ $index + 1 }}</h3>
                      <p class="request-create__item-copy">Choose one material and enter the requested quantity.</p>
                    </div>
                    <app-button type="button" variant="danger" [disabled]="items.length <= 1" (click)="removeItem($index)">
                      <ng-glyph name="delete" size="18"></ng-glyph>
                    </app-button>
                  </div>

                  <div class="request-create__item-grid">
                    <div class="request-create__item-field request-create__item-field--material">
                      <label for="request-pages-create-requestcreatepage-field-5" class="request-create__form-label">Material</label>
                      <app-autocomplete
                        [prefetchOnInit]="true"
                        [options]="materialOptionsByIndex[$index]"
                        [minLength]="1"
                        [debounceMs]="300"
                        placeholder="Search material..."
                        (searchChange)="searchMaterials($event)"
                        (selectionChange)="onMaterialChange($index, $event)"></app-autocomplete>
                      @if (isDuplicateMaterial(getItemMaterialId($index), $index)) {
                      <span class="request-create__error-text">This material is already used in another line.</span>
                      }
                    </div>

                    <div class="request-create__item-field request-create__item-field--qty">
                      <label for="request-pages-create-requestcreatepage-field-6" class="request-create__form-label">Qty</label>
                      <input
                        type="number"
                        min="1"
                        appQtyInput
                        class="request-create__form-input request-create__qty-input"
                        formControlName="qty"
                        (input)="onQtyInput()" />
                    </div>
                  </div>

                  <div class="request-create__stock-hint">
                    @if (!(form.get('warehouseId')?.value ?? '')) {
                    <span class="request-create__stock-empty">Choose a warehouse to preview availability for this line.</span>
                    } @else if (!getItemMaterialId($index)) {
                    <span class="request-create__stock-empty">Choose a material to preview stock for this line.</span>
                    } @else {
                    <div
                      class="request-create__stock-card"
                      [class.request-create__stock-card--ready]="getLineVisualState($index) === 'ready'"
                      [class.request-create__stock-card--warning]="getLineVisualState($index) === 'warning'"
                      [class.request-create__stock-card--unavailable]="getLineVisualState($index) === 'unavailable'"
                      [class.request-create__stock-card--loading]="getLineVisualState($index) === 'loading'">
                      @if (getLineVisualState($index) === 'loading' && !getMaterialPreview($index)) {
                      <div class="request-create__stock-loading">
                        <ng-glyph name="refresh" size="18" class="request-create__spin"></ng-glyph>
                        Checking warehouse availability...
                      </div>
                      } @else {
                      <div
                        class="request-create__stock-metrics"
                        [class.request-create__stock-metrics--muted]="getLineVisualState($index) === 'unavailable'">
                        <div>
                          <span class="request-create__stock-label">On Hand</span>
                          <strong>{{ getMaterialPreview($index)?.onHand ?? 0 }}</strong>
                        </div>
                        <div>
                          <span class="request-create__stock-label">Reserved</span>
                          <strong>{{ getMaterialPreview($index)?.reserved ?? 0 }}</strong>
                        </div>
                        <div>
                          <span class="request-create__stock-label">Available</span>
                          <strong>{{ getMaterialPreview($index)?.available ?? 0 }}</strong>
                        </div>
                      </div>
                      <p class="request-create__stock-status">{{ getLineStatusMessage($index) }}</p>
                      }
                    </div>
                    }
                  </div>
                </div>
                }
              </div>
            </div>
          </section>
        </app-card>

        <app-card>
          <section class="request-create__section">
            <div class="request-create__section-heading">
              <span class="request-create__section-kicker">3</span>
              <div class="request-create__section-heading-main">
                <h2 class="request-create__section-title">Availability Snapshot</h2>
                <p class="request-create__section-copy">
                  Use this to spot constrained lines before the draft moves toward confirmation.
                </p>
              </div>
            </div>

            @if (isFirstPreviewLoad()) {
            <div class="request-create__snapshot-empty">
              <ng-glyph name="refresh" size="20" class="request-create__spin"></ng-glyph>
              Refreshing warehouse stock preview...
            </div>
            } @else {
            <div class="request-create__snapshot-list">
              @for (itemControl of items.controls; track $index) {
              <div class="request-create__snapshot-row">
                <div class="request-create__snapshot-copy">
                  <strong>Line {{ $index + 1 }}</strong>
                  <span class="request-create__snapshot-detail">{{ getReviewMaterialLabel($index) }}</span>
                </div>
                <span
                  class="request-create__snapshot-pill"
                  [class.request-create__snapshot-pill--ready]="getLineVisualState($index) === 'ready'"
                  [class.request-create__snapshot-pill--warning]="getLineVisualState($index) === 'warning'"
                  [class.request-create__snapshot-pill--loading]="getLineVisualState($index) === 'loading'"
                  [class.request-create__snapshot-pill--unavailable]="getLineVisualState($index) === 'unavailable'">
                  {{ getLineStatusLabel($index) }}
                </span>
              </div>
              }
            </div>
            }
          </section>
        </app-card>
      </div>

      <div class="request-create__aside">
        <app-card>
          <aside class="request-create__review">
            <app-summary-panel
              title="Review"
              subtitle="A draft summary you can review before saving."
              [rows]="reviewRows"></app-summary-panel>

            @if (hasDuplicateMaterials) {
            <div class="request-create__review-alert">Remove duplicate materials before saving this request.</div>
            }
          </aside>
        </app-card>
      </div>
    </div>
  </div>
</div>
